/**
 * Storage Connect-RPC Service — BFF 저장소 서비스와의 통신.
 *
 * 객체가 어디에 놓이는지(실행 폴더·모델 폴더·빌드 캐시의 키)는 `runArtifacts.ts` 한 곳이 짓는다. 여기는 그 키로 쓰고
 * (UploadFile·CopyFile), 나열하고(ListFiles), 지우고(DeleteFiles), 읽는다(DownloadFile·결과 목록). BFF 는 키가 호출자의
 * 것인지만 본다.
 */

import { createPromiseClient } from "@connectrpc/connect";
import type { PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { StorageService } from "@/stubs/storage/storage_connect";
import {
  CopyFileRequest,
  DeleteFilesRequest,
  DownloadFileRequest,
  DownloadTaskResultFileRequest,
  ListFilesRequest,
  ListResultFilesRequest,
  StorageLimit,
  UploadFileRequest,
  type CopyFileResponse,
  type ListedObject,
  type ListResultFilesResponse,
  type UploadFileResponse,
} from "@/stubs/storage/storage_pb";
import { BFF_URL, createAuthInterceptor } from "@/app/services/connectCommon";
import { mibLabel } from "@/shared/byteSize";

function validateStorageEnv(env: "development" | "production") {
  const isDev = env === "development";
  const logger = isDev ? console.warn : console.error;
  const envPhrase = isDev ? "" : " in production";
  const hint = isDev
    ? "Check runtime env (window.__ENV) or build-time env (.env)."
    : "Ensure runtime env (env.js) or build-time env are set.";

  if (!BFF_URL) {
    logger(`[StorageService] VITE_BFF_URL is empty${envPhrase}. ${hint}`);
  }
}

if (import.meta.env.DEV) {
  validateStorageEnv("development");
}

if (import.meta.env.PROD) {
  validateStorageEnv("production");
}

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [createAuthInterceptor()],
});

const storageClient: PromiseClient<typeof StorageService> = createPromiseClient(
  StorageService,
  transport
);

export type DownloadedFile = {
  objectKey: string;
  fileName: string;
  contentType: string;
  content: Uint8Array;
};

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  return merged;
}

// ============================================
// Query Operations
// ============================================

/**
 * 결과 파일 목록 조회
 * 경로: /{user_id}/{project_id}/simulation/{simulation_id}/{task_index}/
 */
export async function listResultFiles(
  projectId: string,
  simulationId: string,
  taskIndex: number,
  _modelId?: string,
): Promise<ListResultFilesResponse> {
  const request = new ListResultFilesRequest({
    projectId,
    simulationId,
    modelId: _modelId ?? "",
    taskIndex,
  });
  return await storageClient.listResultFiles(request);
}

// ============================================
// Download Operations
// ============================================

export async function downloadFile(objectKey: string): Promise<DownloadedFile> {
  const stream = storageClient.downloadFile(new DownloadFileRequest({ objectKey }));
  const chunks: Uint8Array[] = [];

  let resolvedObjectKey = objectKey;
  let resolvedFileName = objectKey.split("/").pop() || "download";
  let resolvedContentType = "application/octet-stream";

  for await (const message of stream) {
    if (message.file) {
      resolvedObjectKey = message.file.objectKey || resolvedObjectKey;
      resolvedFileName = message.file.fileName || resolvedFileName;
      resolvedContentType = message.file.contentType || resolvedContentType;
    }
    if (message.chunk && message.chunk.length > 0) {
      chunks.push(message.chunk);
    }
  }

  return {
    objectKey: resolvedObjectKey,
    fileName: resolvedFileName,
    contentType: resolvedContentType,
    content: concatBytes(chunks),
  };
}

/**
 * 시뮬레이션 결과 파일 다운로드
 * 경로: /{user_id}/{project_id}/simulation/{simulation_id}/{task_index}/{file_name}
 *
 * `refuse` 는 첫 메시지의 파일 정보(이름·크기)를 보고 받지 않을 이유를 돌려준다 — 있으면 받기를 끊고 그 오류를 던진다.
 * 다 받은 뒤에 재면 아주 큰 파일은 그 사유보다 탭 메모리가 먼저 다한다(웹 장부 D-221).
 */
export async function downloadTaskResultFile(
  projectId: string,
  simulationId: string,
  taskIndex: number,
  fileName: string,
  modelId?: string,
  refuse?: (file: { fileName: string; size: number }) => Error | null,
): Promise<DownloadedFile> {
  console.log(
    '[downloadTaskResultFile] projectId=%s, simulationId=%s, taskIndex=%d, fileName=%s',
    projectId, simulationId, taskIndex, fileName
  );

  const abort = new AbortController();
  const stream = storageClient.downloadTaskResultFile(
    new DownloadTaskResultFileRequest({
      projectId,
      simulationId,
      modelId: modelId ?? "",
      taskIndex,
      fileName,
    }),
    { signal: abort.signal },
  );
  const chunks: Uint8Array[] = [];

  let resolvedObjectKey = "";
  let resolvedFileName = fileName || "download";
  let resolvedContentType = "application/octet-stream";

  for await (const message of stream) {
    if (message.file) {
      resolvedObjectKey = message.file.objectKey || resolvedObjectKey;
      resolvedFileName = message.file.fileName || resolvedFileName;
      resolvedContentType = message.file.contentType || resolvedContentType;
      const refused = refuse?.({ fileName: resolvedFileName, size: Number(message.file.size) });
      if (refused) {
        abort.abort();
        throw refused;
      }
    }
    if (message.chunk && message.chunk.length > 0) {
      chunks.push(message.chunk);
    }
  }

  return {
    objectKey: resolvedObjectKey,
    fileName: resolvedFileName,
    contentType: resolvedContentType,
    content: concatBytes(chunks),
  };
}

/** 저장소 안에서 복사한다 — 실행 폴더의 입력(빌드 캐시의 FMU·원본 실행의 rstplt·카드 폴더의 로컬 파일) */
export async function copyFile(srcObjectKey: string, dstObjectKey: string): Promise<CopyFileResponse> {
  return await storageClient.copyFile(new CopyFileRequest({ srcObjectKey, dstObjectKey }));
}

// ============================================
// Key Operations — 키(배치)는 runArtifacts.ts 가 짓고, BFF 는 그 키가 호출자의 것인지만 본다
// ============================================

/** 올리기 내용의 상한 — proto 의 선언 하나(StorageLimit). BFF 가 같은 값을 강제한다 */
export const UPLOAD_FILE_MAX_BYTES: number = StorageLimit.UPLOAD_FILE_MAX_BYTES;

/** 그 키에 쓴다(있으면 덮는다). 저장소가 실패를 알리면 던진다 — 쓰지 못한 입력으로 시작이 이어지지 않게.
 *  상한을 넘는 내용은 보내지 않고 던진다 — 큰 파일을 다 보낸 뒤에야 서버의 거절을 받지 않게. 로컬 파일(`Blob`)은 읽기 전에
 *  크기로 잰다 — 다 읽은 뒤에 재면 아주 큰 파일은 그 사유보다 메모리 할당 실패를 먼저 만난다 */
export async function uploadFile(
  objectKey: string,
  content: Blob | Uint8Array | string,
  contentType = "application/octet-stream",
): Promise<UploadFileResponse> {
  const body = typeof content === "string" ? new TextEncoder().encode(content) : content;
  const size = body instanceof Blob ? body.size : body.length;
  if (size > UPLOAD_FILE_MAX_BYTES) {
    const name = objectKey.slice(objectKey.lastIndexOf("/") + 1);
    throw new Error(`${name} 은(는) ${mibLabel(size)} 라 올릴 수 없습니다 — 파일 하나는 ${mibLabel(UPLOAD_FILE_MAX_BYTES)} 까지입니다`);
  }
  const bytes = body instanceof Blob ? new Uint8Array(await body.arrayBuffer()) : new Uint8Array(body);
  const response = await storageClient.uploadFile(new UploadFileRequest({ objectKey, content: bytes, contentType }));
  if (!response.success) throw new Error(response.message || `저장소에 쓰지 못했습니다: ${objectKey}`);
  return response;
}

/** `<접두어>/` 아래 — 재귀면 모든 객체, 아니면 바로 아래(하위 폴더는 `isFolder` 항목, 키가 `/` 로 끝난다) */
export async function listFiles(objectPrefix: string, options: { recursive: boolean }): Promise<ListedObject[]> {
  const response = await storageClient.listFiles(new ListFilesRequest({ objectPrefix, recursive: options.recursive }));
  return response.entries;
}

/** 키들을 지운다 — 없는 키는 오류가 아니다. 저장소가 지우지 못했다고 알린 키를 돌려준다 */
export async function deleteFiles(objectKeys: readonly string[]): Promise<{ deleted: number; failed: string[] }> {
  if (objectKeys.length === 0) return { deleted: 0, failed: [] };
  const response = await storageClient.deleteFiles(new DeleteFilesRequest({ objectKeys: [...objectKeys] }));
  return { deleted: response.deleted, failed: response.failed };
}

/** 폴더(`<접두어>/` 아래 전부)를 지운다 — 지운 수. 하나라도 못 지웠으면 던진다 */
export async function deleteFolder(folderKey: string): Promise<number> {
  const keys = (await listFiles(folderKey, { recursive: true })).map((entry) => entry.objectKey);
  const { deleted, failed } = await deleteFiles(keys);
  if (failed.length > 0) throw new Error(`${folderKey} 의 객체 ${failed.length}개를 지우지 못했습니다`);
  return deleted;
}

// ============================================
// Service object export
// ============================================

export const storageService = {
  listResultFiles,
  downloadFile,
  downloadTaskResultFile,
  copyFile,
  uploadFile,
  listFiles,
  deleteFiles,
  deleteFolder,
};

export default storageService;
